// Config → Quote PDF — manage editable PDF content variants (Lite / Long / custom).
// Admins can rebrand, upload a logo, edit sales copy on pages 2-4, choose which
// pages each variant renders, and set the default variant.
import { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { toast } from "sonner";
import {
  FileText, Save, Plus, Trash2, Star, Upload, Eye, Loader2, X,
} from "lucide-react";

const TABS = [
  ["setup", "Setup & Pages"],
  ["branding", "Branding"],
  ["payment", "Payment & Steps"],
  ["team", "Team"],
  ["whyBuy", "Why Buy (p3)"],
  ["stock", "What We Stock (p4)"],
  ["terms", "Terms Summary"],
];

const ALL_PAGES = [
  [1, "Page 1 · The quote (always on)"],
  [2, "Page 2 · Payment & next steps"],
  [3, "Page 3 · Why buy from us"],
  [4, "Page 4 · What we stock"],
];

export default function QuotePdfContent() {
  const [variants, setVariants] = useState([]);
  const [selId, setSelId] = useState("");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("setup");
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("talmia_token");

  const loadList = async (keepSel) => {
    const { data } = await api.get("/quote-pdf/content");
    const items = data?.items || [];
    setVariants(items);
    const pick = keepSel || (items.find((v) => v.is_default) || items[0])?._id;
    if (pick) setSelId(pick);
    return items;
  };

  useEffect(() => { loadList().finally(() => setLoading(false)); }, []);

  useEffect(() => {
    if (!selId) return;
    setLoading(true);
    api.get(`/quote-pdf/content/${selId}`)
      .then(({ data }) => setDoc(data))
      .catch(() => toast.error("Failed to load variant"))
      .finally(() => setLoading(false));
  }, [selId]);

  // ── nested setter: set(["branding","phone"], value) ──
  const setPath = (path, value) => {
    setDoc((d) => {
      const next = structuredClone(d);
      let o = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (o[path[i]] == null || typeof o[path[i]] !== "object") o[path[i]] = {};
        o = o[path[i]];
      }
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const { _id, ...body } = doc;
      const { data } = await api.put(`/quote-pdf/content/${selId}`, body);
      setDoc(data);
      await loadList(selId);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const createVariant = async () => {
    const name = window.prompt("Name for the new variant (e.g. 'Insulation specialist')");
    if (!name) return;
    try {
      const { data } = await api.post("/quote-pdf/content", { name, pages: [1, 2, 3, 4] });
      await loadList(data._id);
      toast.success("Variant created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const deleteVariant = async () => {
    if (!window.confirm(`Delete variant "${doc?.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/quote-pdf/content/${selId}`);
      const items = await loadList();
      setSelId((items[0] || {})._id || "");
      toast.success("Variant deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const previewSample = async () => {
    try {
      const { data } = await api.get("/quotes?limit=1");
      const q = (data?.items || [])[0];
      if (!q?._id) return toast.error("No quotes available to preview");
      window.open(`/quotes/${q._id}/pdf`, "_blank");
    } catch { toast.error("Could not load a sample quote"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quote-pdf/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPath(["branding", "logo_path"], data.path);
      toast.success("Logo uploaded — save to apply");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const logoUrl = useMemo(() => {
    const p = doc?.branding?.logo_path;
    return p ? `${API}/attachments/${p}?auth=${token}` : null;
     
  }, [doc?.branding?.logo_path, token]);

  if (loading && !doc) {
    return <Layout><div className="p-10 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></Layout>;
  }

  const b = doc?.branding || {};
  const pay = doc?.payment || {};
  const team = doc?.team || {};
  const wb = doc?.why_buy || {};
  const ws = doc?.what_we_stock || {};

  return (
    <Layout>
      <div className="space-y-5 pb-16" data-testid="quote-pdf-content-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-orange-400/80 font-mono">
              <FileText className="w-3.5 h-3.5" /> Config · Quote PDF
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mt-1">Quote PDF Content</h1>
            <p className="text-xs text-slate-400 mt-1">Rebrand, edit sales copy and manage Lite / Long layouts used by the customer PDF.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={previewSample} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="qpc-preview"><Eye className="w-3.5 h-3.5" /> Preview sample</button>
            <button onClick={save} disabled={saving} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg bg-orange-500 text-black font-bold hover:bg-orange-400 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="qpc-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save</button>
          </div>
        </div>

        {/* Variant bar */}
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-white/10 bg-black/30 p-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Variant</span>
          <select value={selId} onChange={(e) => setSelId(e.target.value)} className="text-sm px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100" data-testid="qpc-variant-select">
            {variants.map((v) => <option key={v._id} value={v._id}>{v.name}{v.is_default ? " · default" : ""}</option>)}
          </select>
          {doc && !doc.is_default && (
            <button onClick={() => { setPath(["is_default"], true); toast.message("Marked default — save to apply"); }} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-amber-300 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="qpc-set-default"><Star className="w-3.5 h-3.5" /> Set default</button>
          )}
          {doc?.is_default && <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-1 rounded-md bg-amber-500/15 text-amber-300 inline-flex items-center gap-1"><Star className="w-3 h-3" /> Default</span>}
          <button onClick={createVariant} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="qpc-new"><Plus className="w-3.5 h-3.5" /> New</button>
          {!["long", "lite"].includes(selId) && (
            <button onClick={deleteVariant} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-1.5" data-testid="qpc-delete"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          )}
          <span className="ml-auto text-[10px] font-mono text-slate-500">v{doc?.version} · edited by {doc?.updated_by || "—"}</span>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 flex-wrap border-b border-white/10">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`text-xs font-mono uppercase tracking-widest px-3 py-2 border-b-2 -mb-px ${tab === k ? "border-orange-400 text-orange-300" : "border-transparent text-slate-400 hover:text-slate-200"}`} data-testid={`qpc-tab-${k}`}>{label}</button>
          ))}
        </div>

        {doc && (
          <div className="space-y-4">
            {/* ── SETUP ── */}
            {tab === "setup" && (
              <Card title="Variant setup">
                <Text label="Variant name" value={doc.name} onChange={(v) => setPath(["name"], v)} testid="qpc-f-name" />
                <Area label="Description (internal)" value={doc.description} onChange={(v) => setPath(["description"], v)} rows={2} />
                <div className="mt-4">
                  <FieldLabel>Pages to render</FieldLabel>
                  <div className="space-y-1.5 mt-1">
                    {ALL_PAGES.map(([n, label]) => {
                      const on = (doc.pages || []).includes(n);
                      return (
                        <label key={n} className="flex items-center gap-2 text-sm text-slate-200">
                          <input type="checkbox" checked={on} disabled={n === 1}
                            onChange={(e) => {
                              const set = new Set(doc.pages || []);
                              e.target.checked ? set.add(n) : set.delete(n);
                              set.add(1);
                              setPath(["pages"], [...set].sort());
                            }}
                            data-testid={`qpc-page-${n}`} />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}

            {/* ── BRANDING ── */}
            {tab === "branding" && (
              <Card title="Branding & company details">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Text label="Company name" value={b.company_name} onChange={(v) => setPath(["branding", "company_name"], v)} testid="qpc-f-company" />
                  <Text label="VAT number" value={b.vat} onChange={(v) => setPath(["branding", "vat"], v)} />
                  <Text label="Phone" value={b.phone} onChange={(v) => setPath(["branding", "phone"], v)} />
                  <Text label="Email" value={b.email_addr} onChange={(v) => setPath(["branding", "email_addr"], v)} />
                </div>
                <Text label="Address" value={b.address} onChange={(v) => setPath(["branding", "address"], v)} />

                <div className="mt-4">
                  <FieldLabel>Logo (replaces default mark on the masthead)</FieldLabel>
                  <div className="flex items-center gap-3 mt-1">
                    {logoUrl && <img src={logoUrl} alt="logo" className="h-12 bg-neutral-900 rounded border border-white/10 p-1" />}
                    <label className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5 cursor-pointer" data-testid="qpc-logo-upload">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                    </label>
                    {b.logo_path && <button onClick={() => setPath(["branding", "logo_path"], null)} className="text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 text-xs"><X className="w-3.5 h-3.5" /> Remove</button>}
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                  <ColorField label="Accent (orange)" value={b.colors?.orange} onChange={(v) => setPath(["branding", "colors", "orange"], v)} />
                  <ColorField label="Ink (dark)" value={b.colors?.ink} onChange={(v) => setPath(["branding", "colors", "ink"], v)} />
                  <ColorField label="Gold" value={b.colors?.gold} onChange={(v) => setPath(["branding", "colors", "gold"], v)} />
                </div>
              </Card>
            )}

            {/* ── PAYMENT ── */}
            {tab === "payment" && (
              <>
                <Card title="Bank details">
                  <p className="text-[11px] text-slate-500 mb-2">Rows with dynamic value <code>ref</code> / <code>total</code> auto-fill from the quote.</p>
                  <ObjList items={pay.bank_details} onChange={(v) => setPath(["payment", "bank_details"], v)} fields={[["k", "Label"], ["v", "Value"]]} testid="qpc-bank" blank={{ k: "", v: "" }} />
                </Card>
                <Card title="Ways to pay">
                  <ObjList items={pay.ways_to_pay} onChange={(v) => setPath(["payment", "ways_to_pay"], v)} fields={[["k", "Label"], ["v", "Value"]]} testid="qpc-wtp" blank={{ k: "", v: "" }} />
                  <Area label="Footnote" value={pay.ways_to_pay_note} onChange={(v) => setPath(["payment", "ways_to_pay_note"], v)} rows={2} />
                </Card>
                <Card title="Next steps (3 recommended)">
                  <ObjList items={pay.next_steps} onChange={(v) => setPath(["payment", "next_steps"], v)} fields={[["title", "Title"], ["body", "Body"]]} testid="qpc-steps" blank={{ title: "", body: "" }} />
                </Card>
                <Card title="Price-hold promise">
                  <Text label="Title" value={pay.promise?.title} onChange={(v) => setPath(["payment", "promise", "title"], v)} />
                  <Area label="Body" value={pay.promise?.body} onChange={(v) => setPath(["payment", "promise", "body"], v)} rows={3} />
                </Card>
              </>
            )}

            {/* ── TEAM ── */}
            {tab === "team" && (
              <Card title="Your dedicated trade team">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Text label="Heading" value={team.heading} onChange={(v) => setPath(["team", "heading"], v)} />
                  <Text label="Sub-heading" value={team.subheading} onChange={(v) => setPath(["team", "subheading"], v)} />
                  <Text label="Lead name" value={team.lead?.name} onChange={(v) => setPath(["team", "lead", "name"], v)} />
                  <Text label="Lead role" value={team.lead?.role} onChange={(v) => setPath(["team", "lead", "role"], v)} />
                </div>
                <div className="mt-4"><FieldLabel>Members</FieldLabel>
                  <ObjList items={team.members} onChange={(v) => setPath(["team", "members"], v)} fields={[["name", "Name"], ["role", "Role"], ["note", "Note"]]} testid="qpc-team" blank={{ name: "", role: "", note: "" }} />
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <Text label="WhatsApp title" value={team.whatsapp?.title} onChange={(v) => setPath(["team", "whatsapp", "title"], v)} />
                  <Text label="WhatsApp role" value={team.whatsapp?.role} onChange={(v) => setPath(["team", "whatsapp", "role"], v)} />
                  <Text label="WhatsApp number" value={team.whatsapp?.number} onChange={(v) => setPath(["team", "whatsapp", "number"], v)} />
                  <Text label="WhatsApp link" value={team.whatsapp?.link} onChange={(v) => setPath(["team", "whatsapp", "link"], v)} />
                </div>
              </Card>
            )}

            {/* ── WHY BUY ── */}
            {tab === "whyBuy" && (
              <>
                <Card title="Header">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Text label="Title prefix" value={wb.title_prefix} onChange={(v) => setPath(["why_buy", "title_prefix"], v)} />
                    <Text label="Title emphasis" value={wb.title_em} onChange={(v) => setPath(["why_buy", "title_em"], v)} />
                  </div>
                  <Area label="Intro" value={wb.intro} onChange={(v) => setPath(["why_buy", "intro"], v)} rows={3} />
                </Card>
                <Card title="Stats band (4)">
                  <ObjList items={wb.stats} onChange={(v) => setPath(["why_buy", "stats"], v)} fields={[["v", "Value"], ["k", "Label"]]} testid="qpc-stats" blank={{ v: "", k: "" }} />
                </Card>
                <Card title="Delivery bullets"><BulletList items={wb.delivery} onChange={(v) => setPath(["why_buy", "delivery"], v)} testid="qpc-deliv" /></Card>
                <Card title="Collection bullets"><BulletList items={wb.collection} onChange={(v) => setPath(["why_buy", "collection"], v)} testid="qpc-collect" /></Card>
                <Card title="Trust / reviews">
                  <ObjList items={wb.trust} onChange={(v) => setPath(["why_buy", "trust"], v)} fields={[["name", "Name"], ["url", "URL"], ["pill", "Pill"]]} testid="qpc-trust" blank={{ name: "", url: "", pill: "" }} />
                </Card>
                <Card title="Beat-my-quote block">
                  <Text label="Condition" value={wb.beat_block?.condition} onChange={(v) => setPath(["why_buy", "beat_block", "condition"], v)} />
                  <Text label="Title" value={wb.beat_block?.title} onChange={(v) => setPath(["why_buy", "beat_block", "title"], v)} />
                  <Area label="Body (HTML ok)" value={wb.beat_block?.body} onChange={(v) => setPath(["why_buy", "beat_block", "body"], v)} rows={3} />
                </Card>
                <Card title="Lock-rates CTA">
                  <Text label="Title" value={wb.lock_cta?.title} onChange={(v) => setPath(["why_buy", "lock_cta", "title"], v)} />
                  <Area label="Body" value={wb.lock_cta?.body} onChange={(v) => setPath(["why_buy", "lock_cta", "body"], v)} rows={2} />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Text label="Button text" value={wb.lock_cta?.button_big} onChange={(v) => setPath(["why_buy", "lock_cta", "button_big"], v)} />
                    <Text label="Number" value={wb.lock_cta?.number} onChange={(v) => setPath(["why_buy", "lock_cta", "number"], v)} />
                  </div>
                </Card>
              </>
            )}

            {/* ── WHAT WE STOCK ── */}
            {tab === "stock" && (
              <>
                <Card title="Header">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Text label="Title prefix" value={ws.title_prefix} onChange={(v) => setPath(["what_we_stock", "title_prefix"], v)} />
                    <Text label="Title emphasis" value={ws.title_em} onChange={(v) => setPath(["what_we_stock", "title_em"], v)} />
                  </div>
                  <Area label="Intro" value={ws.intro} onChange={(v) => setPath(["what_we_stock", "intro"], v)} rows={3} />
                </Card>
                <Card title="Stock categories">
                  <ObjList items={ws.cells} onChange={(v) => setPath(["what_we_stock", "cells"], v)} fields={[["title", "Title"], ["cat", "Category"], ["body", "Body"]]} testid="qpc-cells" blank={{ title: "", cat: "", body: "" }} />
                </Card>
                <Card title="Specialist / POA">
                  <Text label="Section title" value={ws.poa_title} onChange={(v) => setPath(["what_we_stock", "poa_title"], v)} />
                  <ObjList items={ws.poa_cols} onChange={(v) => setPath(["what_we_stock", "poa_cols"], v)} fields={[["title", "Title"], ["body", "Body (HTML ok)"]]} testid="qpc-poa" blank={{ title: "", body: "" }} />
                  <Area label="POA note (HTML ok)" value={ws.poa_note} onChange={(v) => setPath(["what_we_stock", "poa_note"], v)} rows={3} />
                </Card>
                <Card title="Final CTA">
                  <Text label="Title" value={doc.final_cta?.title} onChange={(v) => setPath(["final_cta", "title"], v)} />
                  <Area label="Body" value={doc.final_cta?.body} onChange={(v) => setPath(["final_cta", "body"], v)} rows={2} />
                </Card>
              </>
            )}

            {/* ── TERMS ── */}
            {tab === "terms" && (
              <Card title="Terms of business (summary)">
                <Area label="Full summary paragraph" value={doc.terms_summary} onChange={(v) => setPath(["terms_summary"], v)} rows={10} testid="qpc-f-terms" />
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ─────────────────────── reusable field components ──────────────────────
function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}
function FieldLabel({ children }) {
  return <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{children}</span>;
}
function Text({ label, value, onChange, testid }) {
  return (
    <label className="block mb-3">
      <FieldLabel>{label}</FieldLabel>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100 focus:outline-none focus:border-orange-400" />
    </label>
  );
}
function Area({ label, value, onChange, rows = 3, testid }) {
  return (
    <label className="block mb-3">
      <FieldLabel>{label}</FieldLabel>
      <textarea value={value || ""} rows={rows} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100 focus:outline-none focus:border-orange-400 leading-relaxed" />
    </label>
  );
}
function ColorField({ label, value, onChange }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1 flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-white/10 bg-transparent" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full text-sm px-2 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100 font-mono" />
      </div>
    </label>
  );
}
function ObjList({ items = [], onChange, fields, blank, testid }) {
  const rows = items || [];
  const setRow = (i, key, val) => { const next = structuredClone(rows); next[i] = { ...next[i], [key]: val }; onChange(next); };
  const add = () => onChange([...rows, { ...blank }]);
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((row, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, minmax(0,1fr))` }}>
            {fields.map(([key, ph]) => (
              <input key={key} value={row[key] ?? ""} placeholder={ph} onChange={(e) => setRow(i, key, e.target.value)}
                className="text-sm px-2.5 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100 focus:outline-none focus:border-orange-400" data-testid={`${testid}-${i}-${key}`} />
            ))}
          </div>
          <button onClick={() => del(i)} className="mt-1 text-rose-300 hover:text-rose-200" data-testid={`${testid}-del-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={add} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" /> Add row</button>
    </div>
  );
}
function BulletList({ items = [], onChange, testid }) {
  const rows = items || [];
  const setRow = (i, val) => { const next = [...rows]; next[i] = val; onChange(next); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={row ?? ""} onChange={(e) => setRow(i, e.target.value)} className="flex-1 text-sm px-2.5 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-100 focus:outline-none focus:border-orange-400" data-testid={`${testid}-${i}`} />
          <button onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="text-rose-300 hover:text-rose-200"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...rows, ""])} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-lg border border-white/10 text-slate-200 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" /> Add bullet</button>
    </div>
  );
}
